import { isBlank, parseYaml, serializeToPrettyYaml } from "utils/valueTransformers";

/**
 * Which concrete UI control the `default_value` editor should render for a given
 * attribute datatype. The DefaultValueEditor form component maps each datatype to
 * one of these kinds and renders the datatype-appropriate control (a boolean
 * switch, a number input, a date/time picker, a dropdown of allowed values, …)
 * instead of a generic YAML/JSON editor.
 */
export type DefaultValueControlKind =
    | "text"
    | "textarea"
    | "number"
    | "boolean"
    | "date"
    | "datetime"
    | "time"
    | "select"
    | "json"
    | "reference";

export const controlKindForDatatype = (datatype?: string | null): DefaultValueControlKind => {
    switch (datatype) {
        case "STRING":
            return "text";
        case "TEXT":
        case "MARKDOWN":
            return "textarea";
        case "NUMBER":
            return "number";
        case "BOOLEAN":
            return "boolean";
        case "DATE":
            return "date";
        case "DATETIME":
            return "datetime";
        case "TIME":
            return "time";
        case "ALLOWED_VALUES":
            return "select";
        case "RECORD_REFERENCE":
        case "OBJECT_TEMPLATE_REFERENCE":
            return "reference";
        case "JSON":
        case "BYTES":
        default:
            // JSON/BYTES and any unknown/future datatype fall back to the raw
            // YAML/JSON editor, which can express any value shape.
            return "json";
    }
};

/**
 * Sentinel the DefaultValueEditor commits into the form field when the user's raw
 * YAML/JSON text (for a JSON-kind attribute) does not parse. It carries the raw
 * draft so the editor keeps showing exactly what was typed, and is detected by
 * `validateDefaultValue` so the form blocks submit with a clear error instead of
 * silently dropping the input. It is intentionally an object shape that a real
 * parsed value can never collide with.
 */
const INVALID_DRAFT_KEY = "__invalidDefaultValueDraft__";

export const makeInvalidDraft = (raw: string): { [INVALID_DRAFT_KEY]: string } => ({
    [INVALID_DRAFT_KEY]: raw,
});

export const isInvalidDraft = (value: unknown): value is { [INVALID_DRAFT_KEY]: string } =>
    typeof value === "object" &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, INVALID_DRAFT_KEY);

export const getInvalidDraftRaw = (value: unknown): string =>
    isInvalidDraft(value) ? (value as Record<string, string>)[INVALID_DRAFT_KEY] : "";

/**
 * Helpers for the OBJECT_TEMPLATE_ATTRIBUTE `default_value` form field.
 *
 * `default_value` is stored as SchemalessData (arbitrary JSON) on the attribute
 * and is applied by the backend at custom-object creation when the attribute is
 * omitted. In the UI it is edited through a multiline YAML/JSON text field, so we
 * need to (de)serialize between the field's string value and the stored structured
 * value. For a RECORD_REFERENCE / OBJECT_TEMPLATE_REFERENCE attribute the default
 * is the target record id (picked via a record dropdown); the backend validates
 * that the id exists and matches the attribute's reference constraints.
 *
 * These are kept in a dedicated, dependency-light module so they can be unit
 * tested without importing the JSX-heavy resource config.
 */

/**
 * Whether a `default_value` form-field value represents "no default".
 *
 * The field is initialised to `null` (no default), but react-final-form renders
 * an absent/`null` field value as the empty string `""`, so the editor receives
 * `""` — not `null` — when there is no default. The datatype-aware controls also
 * map every empty control state to `null`, so `""` can only ever mean "unset".
 * Treating `null`/`undefined`/blank-string uniformly as unset keeps the read-side
 * display (e.g. the BOOLEAN "no default" label and the Clear button visibility)
 * consistent with that write-side contract. A real, meaningful value — including
 * the boolean `false` or the number `0` — is NOT blank and stays shown.
 */
export const isUnsetDefaultValue = (value: unknown): boolean =>
    value == null || (typeof value === "string" && isBlank(value));

export const REFERENCE_DATATYPES = ["RECORD_REFERENCE", "OBJECT_TEMPLATE_REFERENCE"];

export const isReferenceDatatype = (datatype?: string | null): boolean =>
    !!datatype && REFERENCE_DATATYPES.includes(datatype);

/**
 * Editor value -> stored SchemalessData.
 * An empty / blank editor means "no default" and maps to `null`.
 * A non-string value (e.g. an already-structured value round-tripped through a
 * form that never rendered the editor) is passed through unchanged.
 */
export const parseDefaultValue = (value: unknown): unknown => {
    if (value == null) return null;
    if (typeof value !== "string") return value;
    if (isBlank(value)) return null;
    return parseYaml(value);
};

/**
 * Stored SchemalessData -> editor value.
 * `null` / `undefined` (no default) maps to an empty editor.
 */
export const serializeDefaultValue = (value: unknown): string =>
    value == null ? "" : serializeToPrettyYaml(value);

/**
 * Serialize a stored default value into the JSON/YAML text-editor draft string
 * (for JSON/BYTES-kind attributes). `null`/`undefined` -> empty editor; an
 * invalid-draft sentinel -> the raw text the user typed (so the editor keeps
 * showing it); anything else -> pretty YAML.
 */
export const serializeJsonDraft = (value: unknown): string => {
    if (value == null) return "";
    if (isInvalidDraft(value)) return getInvalidDraftRaw(value);
    return serializeToPrettyYaml(value);
};

/**
 * Parse a JSON/YAML text-editor draft into the stored value for a JSON-kind
 * attribute. Blank -> null (no default); valid YAML/JSON -> the parsed value;
 * anything that fails to parse -> an invalid-draft sentinel (so submit is blocked
 * by validation rather than silently dropping the input).
 */
export const parseJsonDraft = (raw: string): unknown => {
    if (raw == null || isBlank(raw)) return null;
    try {
        return parseYaml(raw);
    } catch {
        return makeInvalidDraft(raw);
    }
};

/**
 * Validate a (structured) default value against the selected datatype.
 * - Reference datatypes cannot carry a default_value.
 * - JSON-kind attributes reject an unparseable draft (invalid-draft sentinel, or
 *   a leftover raw string that does not parse).
 * - NUMBER attributes reject a non-numeric value.
 * Accepts both structured values (from the datatype-aware controls) and raw
 * strings (legacy / tests), so it is safe wherever it is called.
 */
export const validateDefaultValue = (
    value: unknown,
    datatype?: string | null
): string | undefined => {
    if (isReferenceDatatype(datatype)) {
        // A reference default is the target record id (a plain string), or null
        // for "no default". The backend validates that the id exists and matches
        // the attribute's reference constraints (record type / object template);
        // here we only guard the shape so a structured value can't be submitted.
        const isEmpty = value == null || (typeof value === "string" && isBlank(value));
        if (isEmpty) return undefined;
        return typeof value === "string"
            ? undefined
            : "Select a record to use as the default reference.";
    }
    const kind = controlKindForDatatype(datatype);
    if (kind === "json") {
        if (isInvalidDraft(value)) return "Enter valid YAML/JSON, or leave empty for no default.";
        if (typeof value === "string" && !isBlank(value)) {
            try {
                parseYaml(value);
            } catch {
                return "Enter valid YAML/JSON, or leave empty for no default.";
            }
        }
        return undefined;
    }
    if (kind === "number") {
        if (value == null || value === "") return undefined;
        const asNumber = typeof value === "number" ? value : Number(value);
        return Number.isFinite(asNumber) ? undefined : "Enter a valid number.";
    }
    return undefined;
};
